import React, { useState } from 'react'
import { Send, Bell, CheckCircle, AlertCircle } from 'lucide-react'
import { API_BASE } from '../config'

function getMonthKey() {
  const d = new Date()
  return `trial_notif_count_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
function getMonthlyNotifCount() {
  try { return parseInt(localStorage.getItem(getMonthKey()) || '0', 10) } catch { return 0 }
}
function incrementMonthlyNotifCount() {
  try { localStorage.setItem(getMonthKey(), String(getMonthlyNotifCount() + 1)) } catch {}
}

export default function BroadcastNotification({ trialMaxPerMonth, isTrial }) {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null) // { type: 'success'|'error', message: '' }
  const [monthlyCount, setMonthlyCount] = useState(() => getMonthlyNotifCount())

  const atLimit = trialMaxPerMonth != null && trialMaxPerMonth >= 0 && monthlyCount >= trialMaxPerMonth

  // Preset templates
  const templates = [
    { label: '💰 Payment Reminder', title: 'Maintenance Dues Reminder', body: 'Kindly pay your community maintenance charges by the 5th of this month. Thank you!' },
    { label: '🔧 Maintenance Notice', title: 'Scheduled Maintenance', body: 'There will be a scheduled maintenance activity on common areas this weekend. We apologize for the inconvenience.' },
    { label: '🚰 Water Supply', title: 'Water Supply Update', body: 'Water supply will be interrupted tomorrow from 10 AM to 2 PM due to tank cleaning. Please store water in advance.' },
    { label: '📢 General Announcement', title: 'Community Notice', body: '' },
  ]

  async function handleSend() {
    if (atLimit) return
    if (!title.trim() || !body.trim()) {
      setResult({ type: 'error', message: 'Please enter both title and message' })
      return
    }

    setSending(true)
    setResult(null)

    try {
      const idToken = sessionStorage.getItem('id_token')
      const communityId = sessionStorage.getItem('community_id')

      if (!idToken || !communityId) {
        setResult({ type: 'error', message: 'Not authenticated or missing community' })
        setSending(false)
        return
      }

      const resp = await fetch(`${API_BASE}/broadcast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          COMMUNITY_ID: communityId,
          TITLE: title.trim(),
          BODY: body.trim()
        })
      })

      if (!resp.ok) {
        const errText = await resp.text()
        setResult({ type: 'error', message: errText || 'Failed to send notification' })
        setSending(false)
        return
      }

      const data = await resp.json()
      incrementMonthlyNotifCount()
      const newCount = getMonthlyNotifCount()
      setMonthlyCount(newCount)
      setResult({
        type: 'success',
        message: `Notification sent to ${data.endpoints || 0} device(s)${trialMaxPerMonth != null ? ` · ${trialMaxPerMonth - newCount} remaining this month` : ''}`
      })
      setTitle('')
      setBody('')
    } catch (err) {
      console.error('broadcast error', err)
      setResult({ type: 'error', message: 'Network error. Please try again.' })
    }
    setSending(false)
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Bell size={20} className="text-blue-600" />
        <h2 className="text-lg font-bold">Broadcast Notification</h2>
      </div>

      <p className="text-sm">
        Send a push notification to all residents in your community.
      </p>

      {/* Trial limit banner */}
      {isTrial !== false && trialMaxPerMonth != null && trialMaxPerMonth >= 0 && (
        <div style={{
          background: atLimit ? '#fef2f2' : '#fffbeb',
          border: `1px solid ${atLimit ? '#fca5a5' : '#fbbf24'}`,
          borderRadius: 8, padding: '10px 14px', fontSize: 13,
          color: atLimit ? '#991b1b' : '#92400e'
        }}>
          {atLimit
            ? `\u{1F512} You've used all ${trialMaxPerMonth} notifications for this month on your free trial. Upgrade to send more.`
            : `\u{1F514} Trial: ${monthlyCount} / ${trialMaxPerMonth} notifications used this month`}
        </div>
      )}

      {/* Quick Templates */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Quick Templates</label>
        <div className="flex flex-wrap gap-2">
          {templates.map((t, i) => (
            <button
              key={i}
              onClick={() => { setTitle(t.title); setBody(t.body); setResult(null) }}
              className="text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 border border-blue-200"
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Title Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notification Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setResult(null) }}
          placeholder="e.g., Payment Reminder"
          maxLength={100}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Body Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
        <textarea
          value={body}
          onChange={(e) => { setBody(e.target.value); setResult(null) }}
          placeholder="Type your message here..."
          rows={4}
          maxLength={500}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-400 mt-1 text-right">{body.length}/500</p>
      </div>

      {/* Result */}
      {result && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
          result.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {result.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {result.message}
        </div>
      )}

      {/* Send Button */}
      <button
        onClick={handleSend}
        disabled={sending || !title.trim() || !body.trim() || atLimit}
        className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-lg text-sm font-medium disabled:opacity-50 active:bg-blue-700"
      >
        {sending ? (
          <>
            <span className="animate-spin">⏳</span>
            Sending...
          </>
        ) : (
          <>
            <Send size={16} />
            Send to All Residents
          </>
        )}
      </button>
    </div>
  )
}
